import dayjs from "dayjs";
import { METHOD_ICONS } from "../icons";
import type {
  CropFootprint,
  CropListPayload,
  CropNote,
  CropPayload,
  CropMethod,
  CropVarietyPayload,
  TaxonLite,
} from "@/types";
import { SEEDING_METHODS } from "@/types";
import Task from "./task";

export const METHODS: Record<string, string> = {
  SEEDING_IN_GROUND: "Semis en pleine terre",
  SEEDING_INHOUSE: "Semis au chaud (maison / serre chauffée)",
  SEEDING_ON_HOT_BED: "Sur couche chaude",
  PLANT: "Achat de plants",
  EXISTING: "Déjà en place",
};

export function getMethodIcon(method: string): string {
  return METHOD_ICONS[method.toLowerCase()];
}

// Lean crop from the LIST route — everything the crop list and the garden plan
// need. The detail-only data (tasks, affinities) lives on `Crop` below.
export class CropListItem {
  id: number;
  area: number;
  methods: CropMethod[];
  taxon: number;
  varieties: CropVarietyPayload[];
  isOrganic: boolean;
  startOn: Date;
  endOn: Date | null;
  // Effective garden-entry date (read-only, backend-derived): when the crop actually
  // occupies a cell (transplant, or = startOn for a direct sow).
  gardenStartOn: Date;
  methodsDisplay: string[];
  methodsIcons: string[];
  // Cultivation year.
  year: number;
  // Footprint of one plant (specimen) at the queried garden's resolution — null
  // unless the crops were fetched with ?cell_size=. Rectangular; a plant occupies
  // width × height cells (see `footprintCells`).
  footprint: CropFootprint | null;
  // Backend-computed expected counts at the queried garden's resolution — both null
  // unless the crops were fetched with ?cell_size=. `expectedSpecimens` = number of
  // plants to place; `expectedCells` = total cells they claim.
  expectedSpecimens: number | null;
  expectedCells: number | null;

  constructor({
    id,
    taxon,
    varieties,
    area,
    methods,
    is_organic: isOrganic,
    start_on: startOn,
    garden_start_on: gardenStartOn,
    end_on: endOn,
    year,
    footprint,
    expected_specimens: expectedSpecimens,
    expected_cells: expectedCells,
  }: CropListPayload) {
    this.id = id;
    // API sends area as a string (Django DecimalField); normalize to a number here.
    this.area = Number(area);
    this.methods = methods;
    this.taxon = taxon;
    this.varieties = varieties;
    this.isOrganic = isOrganic;
    this.startOn = new Date(Date.parse(startOn));
    // Falls back to startOn if the backend ever omits it (older payloads).
    this.gardenStartOn = new Date(Date.parse(gardenStartOn ?? startOn));
    this.endOn = endOn ? new Date(Date.parse(endOn)) : null;
    this.year = year;
    this.footprint = footprint ?? null;
    this.expectedSpecimens = expectedSpecimens ?? null;
    this.expectedCells = expectedCells ?? null;

    this.methodsDisplay = this.methods.map((method) => METHODS[method]);
    this.methodsIcons = this.methods.map((method) => getMethodIcon(method));
  }

  get isSeeded(): boolean {
    return this.methods.some((m) => SEEDING_METHODS.includes(m));
  }

  // A start→end window as a human label, month + year ("De mai à octobre 2025"). The
  // start year is dropped when it matches the end year; open-ended windows (no end →
  // perennials / already in place) read "Depuis mai 2025".
  private static periodLabel(start: Date, end: Date | null): string {
    const month = (d: Date) => d.toLocaleDateString("fr-FR", { month: "long" });
    const monthYear = (d: Date) => d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    if (!end) return `Depuis ${monthYear(start)}`;
    const startLabel = start.getFullYear() === end.getFullYear() ? month(start) : monthYear(start);
    return `De ${startLabel} à ${monthYear(end)}`;
  }

  // Cultivation window (from the sowing date).
  get cultivationPeriodLabel(): string {
    return CropListItem.periodLabel(this.startOn, this.endOn);
  }

  // Garden-occupancy window (from the garden-entry/transplant date) — when the plant
  // actually sits on the plan. This is the window the plan editor displays.
  get gardenPeriodLabel(): string {
    return CropListItem.periodLabel(this.gardenStartOn, this.endOn);
  }

  // Machine-readable garden-occupancy window (ISO `YYYY-MM-DD`): garden-entry date →
  // end (null = open-ended). This is the window the plan keys cell display off while
  // this crop is selected for placement; it pairs with `overlapsWindow`.
  get occupancyWindow(): { start: string; end: string | null } {
    return {
      start: dayjs(this.gardenStartOn).format("YYYY-MM-DD"),
      end: this.endOn ? dayjs(this.endOn).format("YYYY-MM-DD") : null,
    };
  }

  getCellsCount(cellSize: number): number {
    // cellSize in meters. Any positive area claims at least one cell.
    if (this.area <= 0) return 0;
    return Math.max(1, Math.round(this.area / cellSize ** 2));
  }

  // Authoritative number of cells a planting of this crop claims: the backend-computed
  // `expectedCells` when available (crops fetched with ?cell_size=), else derived from
  // the area. The plant tool shows this as the target footprint size.
  requiredCells(cellSize: number): number {
    return this.expectedCells ?? this.getCellsCount(cellSize);
  }

  // Number of cells ONE plant (specimen) of this crop occupies = width × height of
  // its footprint. 1 when the footprint is unknown (crops fetched without cell_size).
  get footprintCells(): number {
    return this.footprint ? this.footprint.width * this.footprint.height : 1;
  }
}

// Full crop from the DETAIL route — the lean fields plus tasks and affinities.
export default class Crop extends CropListItem {
  topAffinities: TaxonLite[];
  worstAffinities: TaxonLite[];
  tasks: Task[];
  // Dated observations (most recent first). See the /crop-notes/ resource.
  notes: CropNote[];

  constructor(payload: CropPayload) {
    super(payload);
    this.topAffinities = payload.top_affinities ?? [];
    this.worstAffinities = payload.worst_affinities ?? [];
    this.tasks = (payload.tasks ?? []).map((task) => new Task(task));
    this.notes = payload.notes ?? [];
  }

  get todoTasks(): Task[] {
    return this.tasks.filter((t) => t.status === "TODO");
  }

  get nextTodoTask(): Task | null {
    if (this.todoTasks.length === 0) return null;
    return this.todoTasks.reduce((soonest, t) => (t.dateFrom < soonest.dateFrom ? t : soonest));
  }
}
